import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from '../../../core/base-api.service';
import { ApiResponse, PageableApiResponse } from '../../api-response';
import { Layout, LayoutGroup, LayoutGroupRequest, LayoutRequest } from '../models/layout.model';

@Injectable({ providedIn: 'root' })
export class LayoutService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly layoutGroupUrl = `${this.coreApiUrl}/api/v1/layout-group`;
  private readonly layoutUrl = `${this.coreApiUrl}/api/v1/layout`;
  private readonly deviceUrl = `${this.coreApiUrl}/api/v1/device`;

  // Layout Groups
  getLayoutGroups(page: number, size: number): Observable<PageableApiResponse<LayoutGroup>> {
    return this.http.get<PageableApiResponse<LayoutGroup>>(
      `${this.layoutGroupUrl}?page=${page}&size=${size}`
    );
  }

  createLayoutGroup(body: LayoutGroupRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(this.layoutGroupUrl, body);
  }

  updateLayoutGroup(id: number, body: LayoutGroupRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.layoutGroupUrl}/${id}`, body);
  }

  deleteLayoutGroup(id: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.layoutGroupUrl}/${id}`);
  }

  // Layouts
  getLayoutsForGroup(layoutGroupId: number): Observable<ApiResponse<Layout[]>> {
    return this.http.get<ApiResponse<Layout[]>>(
      `${this.layoutUrl}/for-layout-group/${layoutGroupId}`
    );
  }

  createLayout(layoutGroupId: number, body: LayoutRequest): Observable<ApiResponse<boolean>> {
    return this.http.post<ApiResponse<boolean>>(
      `${this.layoutUrl}/for-layout-group/${layoutGroupId}`,
      body
    );
  }

  updateLayout(id: number, body: LayoutRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.layoutUrl}/${id}`, body);
  }

  deleteLayout(id: number): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.layoutUrl}/${id}`);
  }

  // Device-layout assignment
  assignLayoutGroupToDevice(clientId: string, layoutGroupId: number): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.deviceUrl}/${clientId}/layout-groups`,
      { layoutGroupId }
    );
  }

  removeLayoutGroupFromDevice(clientId: string, layoutGroupId: number): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${this.deviceUrl}/${clientId}/layout-groups/${layoutGroupId}`
    );
  }

  /** Unassigns every layout group currently assigned to the device in one
   *  call. Resolves 200 even if the device had no groups assigned; throws
   *  an HttpErrorResponse (404 if the device itself doesn't exist) otherwise. */
  unassignAllLayoutGroupsFromDevice(clientId: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${this.deviceUrl}/${clientId}/layout-groups`
    );
  }

  // MQTT triggers
  sendAllTemplatesToDevice(clientId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.deviceUrl}/set-all-templates`,
      { clientId }
    );
  }

  sendAllTemplateGroupsToDevice(clientId: string): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.deviceUrl}/set-all-template-groups`,
      { clientId }
    );
  }
}
